import { Metadata } from 'next';

import { CountryCode } from '@/i18n/countries';
import { LanguageCode, languageCodes, languageDefaultCode } from '@/i18n/languages';

import { externalLinks } from './constants';

export const pagePrefixes = [
    '',
    'about',
    'companies',
    'events',
    'collaboration',
    'user',
    'documents',
    'payment-result',
] as const;

export function buildUrl(
    page: (typeof pagePrefixes)[number] | string,
    params?: {
        lang?: LanguageCode;
        children?: string | string[];
        query?: Record<string, string>;
        fragment?: string;
        qualified?: boolean;
    },
): string {
    const clientUrl = params?.lang !== 'sr' ? externalLinks.clientRoot : externalLinks.clientRS;
    let url = params?.qualified ? clientUrl : '';
    url += params?.lang ? '/' + params.lang : '';
    url += page ? '/' + page : '';
    url += params?.children ? '/' + [params.children].flat().join('/') : '';
    if (params?.query) {
        const queryEntries = Object.entries(params.query);
        if (queryEntries.length > 0) {
            const query = queryEntries.map(([key, value]) => key + '=' + value).join('&');
            url += '?' + encodeURI(query);
        }
    }
    url += params?.fragment ? '#' + params.fragment : '';
    return url;
}

export function buildAlternates(
    page: (typeof pagePrefixes)[number] | string,
    params: {
        lang: LanguageCode;
        children?: string | string[];
        countryCode?: CountryCode;
    },
): Metadata['alternates'] {
    const defaultLangUrl = buildUrl(page, {
        lang: params.lang === 'sr' ? params.lang : languageDefaultCode,
        children: params.children,
        qualified: true,
    });

    const languages: { [key: string]: string } = {
        'x-default': defaultLangUrl,
    };

    const alternatePages = languageCodes.filter((langCode) => langCode !== params.lang);
    for (const lang of alternatePages) {
        languages[`${lang}-${params.countryCode}`] = buildUrl(page, {
            lang: lang,
            children: params.children,
            qualified: true,
        });
    }

    return {
        canonical: defaultLangUrl,
        languages: languages,
    };
}
